import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localeDE from '@angular/common/locales/de';
import localeEN from '@angular/common/locales/en-GB';
import {
  IModuleTranslationOptions,
  ModuleTranslateLoader,
} from '@larscom/ngx-translate-module-loader';
import {
  AppContainerModule,
  AppContainerOptions,
  AppContainerService,
  LanguageService,
  MydocmaServiceWorkerModule,
} from '@mydocma/app-container';
import { AuthModule, JwtAuthService } from '@mydocma/auth';
import { ResizeModule } from '@mydocma/component-library';
import {
  CustomMatIconModule,
  CustomMatIconService,
} from '@mydocma/component-library/src/lib/custom-mat-icon';
import { MediaQueryService, MediatorService } from '@mydocma/core';
import { CustomerModule } from '@mydocma/customer';
import { ProjectModule, ProjectStructureService } from '@mydocma/project';
import { CustomTaskFileDownloadService, MydocmaTaskModule } from '@mydocma/task';
import { CustomFileDownloadService } from './services/custom-file-download.service';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MainComponent } from './main/main.component';
import { RouterModule } from '@angular/router';
import { MatDialogModule } from '@angular/material/dialog';
import { ShareDocumentLinkService } from 'projects/mydocma/pr/src/lib/services/share-document-link.service';
import { ShareLinkMainComponent } from './share-link-main/share-link-main.component';
import { DocumentPreviewMainComponent } from './document-preview-main/document-preview-main.component';
import { ShareLinkGuard } from 'projects/mydocma/pr/src/lib/guards/share-link-guard';
import { MyDocmaSystemPagesModule } from '@mydocma/component-library/src/lib/system-pages';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, first, firstValueFrom } from 'rxjs';

const EN = 'en';
const DE = 'de';
@NgModule({
  declarations: [AppComponent, MainComponent, ShareLinkMainComponent, DocumentPreviewMainComponent],
  imports: [
    AppRoutingModule,
    AppContainerModule,
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    MatDialogModule,
    MydocmaTaskModule,
    MydocmaServiceWorkerModule,
    MyDocmaSystemPagesModule,
    ResizeModule,
    RouterModule,
    AuthModule.forRoot(),
    CustomerModule.forRoot(),
    ProjectModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    CustomMatIconModule,
    MatTooltipModule,
  ],
  providers: [
    AppContainerOptions,
    AppContainerService,
    MediaQueryService,
    MediatorService,
    ProjectStructureService,
    JwtAuthService,
    ShareDocumentLinkService,
    ShareLinkGuard,
    { provide: CustomTaskFileDownloadService, useClass: CustomFileDownloadService },
    {
      provide: APP_INITIALIZER,
      useFactory: preloadRemoteIcon,
      deps: [HttpClient],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(languageService: LanguageService, translateService: TranslateService) {
    languageService.setLanguages([
      { id: EN, name: 'English' },
      { id: DE, name: 'Deutsch' },
    ]);

    registerLocaleData(localeDE, DE);

    localeEN[10][0] = 'yy-MM-dd';
    localeEN[10][1] = 'yyyy-MM-dd';

    registerLocaleData(localeEN, EN);

    translateService.addLangs([EN, DE]);
    translateService.setDefaultLang(DE);
    translateService.use(DE);
  }
}

export function createTranslateLoader(http: HttpClient) {
  const baseTranslateUrl = './assets/i18n';
  return new ModuleTranslateLoader(http, <IModuleTranslationOptions>{
    modules: [
      { baseTranslateUrl },
      { moduleName: 'pr', baseTranslateUrl },
      { moduleName: 'app-container', baseTranslateUrl },
      { moduleName: 'comp-lib', baseTranslateUrl },
      { moduleName: 'project', baseTranslateUrl },
      { moduleName: 'customer', baseTranslateUrl },
      { moduleName: 'form', baseTranslateUrl },
      { moduleName: 'comp-task', baseTranslateUrl },
    ],
  });
}

const REMOTE_ICONS = ['high-priority', 'info', 'high-priority_outline', 'preview_no'];

export function preloadRemoteIcon(http: HttpClient) {
  return () =>
    Promise.all(
      REMOTE_ICONS.map((name) =>
        firstValueFrom(
          http.get(`https://assets.edr-software.com/v0/material-icons/${name}.svg`, {
            responseType: 'text',
          }),
        ).catch(() => null),
      ),
    );
}
